import { createSlice, isAction } from '@reduxjs/toolkit'
import type { FundGoal } from 'components/fundGoal'

export interface IMoneyPoolBoxAdd {
    name: string
    // Persisted: a draft saved before this question existed rehydrates
    // without these keys, so read them with `?? ''`.
    goal: FundGoal | ''
    goal_description: string
    memberShipFee: string
    date: string
    dueDate: string
    showOtherRecieverLoans: boolean | undefined
    showOtherWantedLoan: boolean | undefined
    showOtherTransaction: boolean | undefined
    showOverdue: boolean | undefined
    showOthersData: boolean | undefined
    showBalance: boolean | undefined
    showDebt: boolean | undefined
    sendMessageLoanToOthers: boolean | undefined
    sendMessageRememberMemberShip: boolean | undefined
    sendMessageRememberLoan: boolean | undefined
    sendMessageDepositToManager: boolean | undefined
    sendPushNotificationToOthers: boolean | undefined
    sendPushNotificationToAllMembers: boolean | undefined
    help: boolean | undefined
    bankAccount: IBankCardsResult | undefined
    isManagerAccount: boolean | undefined
    image_id: number | undefined
    stage: number
    totalObligationManager: string | undefined
    paidObligationManager: string | undefined
    totalVariousDeposit: string | undefined
    totalVariousCashouts: string | undefined
    moneyPool: undefined | number
    poolShipId: undefined | number
    loans: Array<{
        id: number
        date?: string
        amount: string
        number: string
        tag?: string
        repayment_amount: string
        fee: string
        feeType: string
        selectedFeeType: string
    }>
}
const initialState: IMoneyPoolBoxAdd = {
    moneyPool: undefined,
    poolShipId: undefined,
    name: '',
    goal: '',
    goal_description: '',
    memberShipFee: '',
    date: '',
    dueDate: '',
    image_id: undefined,
    totalObligationManager: undefined,
    paidObligationManager: undefined,
    totalVariousDeposit: undefined,
    totalVariousCashouts: undefined,
    showOtherRecieverLoans: undefined,
    showOtherWantedLoan: undefined,
    showOtherTransaction: undefined,
    showOverdue: undefined,
    showOthersData: undefined,
    showBalance: undefined,
    showDebt: undefined,
    sendMessageLoanToOthers: undefined,
    sendMessageRememberMemberShip: undefined,
    sendMessageRememberLoan: undefined,
    sendMessageDepositToManager: undefined,
    sendPushNotificationToOthers: undefined,
    sendPushNotificationToAllMembers: undefined,
    help: false,
    bankAccount: undefined,
    isManagerAccount: false,
    loans: [],
    stage: 0,
}
export const MoneyPoolBoxAdd = createSlice({
    name: 'MoneyPoolBoxAdd',
    initialState,
    reducers: {
        toggleDetail: (
            state,
            action: {
                payload: {
                    name: string
                    image_id: number | undefined
                    goal: FundGoal
                    goal_description: string
                }
            }
        ) => {
            state.name = action.payload.name
            state.image_id = action.payload.image_id
            state.goal = action.payload.goal
            state.goal_description = action.payload.goal_description
        },
        toggleMemberShip: (
            state,
            action: {
                payload: {
                    memberShipFee: string
                    date: string
                    dueDate: string
                }
            }
        ) => {
            state.memberShipFee = action.payload.memberShipFee
            state.date = action.payload.date
            state.dueDate = action.payload.dueDate
        },
        toggleTransparency: (
            state,
            action: {
                payload: {
                    showOtherRecieverLoans: boolean
                    showOtherWantedLoan: boolean
                    showOtherTransaction: boolean
                    showOverdue: boolean
                    showOthersData: boolean
                    showBalance: boolean
                    showDebt: boolean
                }
            }
        ) => {
            state.showOtherRecieverLoans = action.payload.showOtherRecieverLoans
            state.showOtherWantedLoan = action.payload.showOtherWantedLoan
            state.showOtherTransaction = action.payload.showOtherTransaction
            state.showOverdue = action.payload.showOverdue
            state.showOthersData = action.payload.showOthersData
            state.showBalance = action.payload.showBalance
            state.showDebt = action.payload.showDebt
        },
        toggleSendMessage: (
            state,
            action: {
                payload: {
                    sendMessageLoanToOthers: boolean
                    sendMessageRememberMemberShip: boolean
                    sendMessageRememberLoan: boolean
                    sendMessageDepositToManager: boolean
                    sendPushNotificationToOthers: boolean
                    sendPushNotificationToAllMembers: boolean
                }
            }
        ) => {
            state.sendMessageLoanToOthers =
                action.payload.sendMessageLoanToOthers
            state.sendMessageRememberMemberShip =
                action.payload.sendMessageRememberMemberShip
            state.sendMessageRememberLoan =
                action.payload.sendMessageRememberLoan
            state.sendMessageDepositToManager =
                action.payload.sendMessageDepositToManager
            state.sendPushNotificationToOthers =
                action.payload.sendPushNotificationToOthers
            state.sendPushNotificationToAllMembers =
                action.payload.sendPushNotificationToAllMembers
        },
        toggleManager: (
            state,
            action: {
                payload: {
                    help: boolean
                    bankAccount: IBankCardsResult | undefined
                    isManagerAccount: boolean
                }
            }
        ) => {
            state.help = action.payload.help
            state.bankAccount = action.payload.bankAccount
            state.isManagerAccount = action.payload.isManagerAccount
        },
        toggleAddNewLoan: (
            state,
            action: {
                payload: {
                    id: number
                    date?: string
                    amount: string
                    number: string
                    tag?: string
                    repayment_amount: string
                    fee: string
                    feeType: string
                    selectedFeeType: string
                }
            }
        ) => {
            state.loans.push(action.payload)
        },
        reset: () => initialState,
        toggleStage: (state, action: { payload: number }) => {
            state.stage = action.payload
        },
        toggleLast: (
            state,
            action: {
                payload: {
                    totalObligationManager: string
                    paidObligationManager: string
                    totalVariousDeposit: string
                    totalVariousCashouts: string
                }
            }
        ) => {
            state.totalObligationManager = action.payload.totalObligationManager
            state.paidObligationManager = action.payload.paidObligationManager
            state.totalVariousDeposit = action.payload.totalVariousDeposit
            state.totalVariousCashouts = action.payload.totalVariousCashouts
        },
        toggleEditLoan: (
            state,
            action: {
                payload: {
                    id: number
                    date?: string
                    amount: string
                    number: string
                    tag?: string
                    repayment_amount: string
                    fee: string
                    feeType: string
                    selectedFeeType: string
                }
            }
        ) => {
            const newLoans = state.loans.filter(
                (item) => item.id !== action.payload.id
            )
            newLoans.push(action.payload)
            state.loans = newLoans
        },
        toggleMoneyPool: (
            state,
            action: { payload: { moneyPool: number; poolShip: number } }
        ) => {
            state.moneyPool = action.payload.moneyPool
            state.poolShipId = action.payload.poolShip
        },
    },
})

export const {
    toggleDetail,
    toggleMemberShip,
    toggleTransparency,
    toggleSendMessage,
    toggleManager,
    reset,
    toggleAddNewLoan,
    toggleStage,
    toggleLast,
    toggleEditLoan,
    toggleMoneyPool,
} = MoneyPoolBoxAdd.actions

export default MoneyPoolBoxAdd.reducer
